import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  salesLeadService,
  type SalesLead,
  type SalesLeadCloseInput,
  type SalesLeadContactInput,
  type SalesLeadCreateInput,
  type SalesLeadExportFormat,
  type SalesLeadExportLayout,
  type SalesLeadExportTab,
  type SalesLeadListParams,
  type SalesLeadRegisterInput,
  type SalesLeadUpdateInput,
  type SalesLeadUpdateRegisteredInput,
} from '@/modules/sales/services/sales-lead.service'
import { useAuth } from '@/hooks/useAuth'
import { isLeadInScope } from '@/modules/sales/utils/lead-access'
import { upsertSalesLeadInCache } from '@/modules/sales/utils/sales-lead-realtime'

export const salesLeadKeys = {
  all: ['sales-leads'] as const,
  list: (params: SalesLeadListParams) => [...salesLeadKeys.all, 'list', params] as const,
  // Distinct key namespace — a different DTO shape (RegisteredLeadListItem), never
  // mixed into the lead-list cache.
  registeredList: (params: Omit<SalesLeadListParams, 'stage'>) =>
    [...salesLeadKeys.all, 'registered-list', params] as const,
  detail: (id: string) => [...salesLeadKeys.all, 'detail', id] as const,
  assignees: ['sales-leads', 'assignees'] as const,
}

function invalidateSalesLeadQueries(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: salesLeadKeys.all })
  if (id) void qc.invalidateQueries({ queryKey: salesLeadKeys.detail(id) })
}

export function useSalesLeads(params: SalesLeadListParams, enabled = true) {
  return useQuery({
    queryKey: salesLeadKeys.list(params),
    queryFn: ({ signal }) => salesLeadService.list(params, { signal }),
    placeholderData: (previous) => previous,
    enabled,
  })
}

/** Registered Leads tab — a dedicated query against the registered-only endpoint
 * shape, kept in its own cache bucket (see salesLeadKeys.registeredList). */
export function useRegisteredSalesLeads(params: Omit<SalesLeadListParams, 'stage'>, enabled = true) {
  return useQuery({
    queryKey: salesLeadKeys.registeredList(params),
    queryFn: ({ signal }) => salesLeadService.listRegistered(params, { signal }),
    placeholderData: (previous) => previous,
    enabled,
  })
}

/** Same filter state powering the on-screen list, exported as XLSX/PDF —
 * never a second query/pagination stack. */
export function useExportSalesLeads() {
  return useMutation({
    mutationFn: ({
      params,
      format,
      tab,
      layout,
    }: {
      params: SalesLeadListParams
      format: SalesLeadExportFormat
      tab: SalesLeadExportTab
      layout?: SalesLeadExportLayout
    }) => salesLeadService.export(params, format, tab, layout),
  })
}

export function useSalesLeadAssignees(enabled = true) {
  return useQuery({
    queryKey: salesLeadKeys.assignees,
    queryFn: ({ signal }) => salesLeadService.assignees({ signal }),
    staleTime: 60_000,
    enabled,
  })
}

export function useSalesLead(id: string | undefined) {
  return useQuery({
    queryKey: salesLeadKeys.detail(id ?? ''),
    queryFn: ({ signal }) => salesLeadService.getById(id!, { signal }),
    enabled: Boolean(id),
  })
}

function useUpsertOnSuccess() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return (lead: SalesLead) => {
    if (!isLeadInScope(user, lead)) {
      qc.removeQueries({ queryKey: salesLeadKeys.detail(lead.id) })
      invalidateSalesLeadQueries(qc)
      return
    }
    // The actor never gets a WebSocket event for their own mutation — apply the response directly.
    // Detail is already exact from the response, so only the broad list invalidate is needed
    // (it also corrects any cached filtered view the patched row no longer belongs in).
    upsertSalesLeadInCache(qc, lead)
    invalidateSalesLeadQueries(qc)
  }
}

export function useCreateSalesLead() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: (input: SalesLeadCreateInput) => salesLeadService.create(input),
    onSuccess: (result) => onLead(result.lead),
  })
}

/** Field edits and simple pipeline moves (in_progress/follow_up/cancelled, or
 * reopening a cancelled lead) both go through this — same PATCH endpoint. */
export function useUpdateSalesLead() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadUpdateInput }) =>
      salesLeadService.update(id, input),
    onSuccess: (result) => onLead(result.lead),
  })
}

export function useAddSalesLeadRemark() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      salesLeadService.addRemark(id, body),
    onSuccess: onLead,
  })
}

export function useAddSalesLeadContact() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadContactInput }) =>
      salesLeadService.addContact(id, input),
    onSuccess: onLead,
  })
}

export function useUpdateSalesLeadRemark() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, remarkId, body }: { id: string; remarkId: string; body: string }) =>
      salesLeadService.updateRemark(id, remarkId, body),
    onSuccess: onLead,
  })
}

export function useDeleteSalesLeadRemark() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, remarkId }: { id: string; remarkId: string }) =>
      salesLeadService.deleteRemark(id, remarkId),
    onSuccess: onLead,
  })
}

export function useUpdateSalesLeadContact() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({
      id,
      contactId,
      input,
    }: {
      id: string
      contactId: string
      input: SalesLeadContactInput
    }) => salesLeadService.updateContact(id, contactId, input),
    onSuccess: onLead,
  })
}

export function useDeleteSalesLeadContact() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, contactId }: { id: string; contactId: string }) =>
      salesLeadService.deleteContact(id, contactId),
    onSuccess: onLead,
  })
}

export function useRegisterSalesLead() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadRegisterInput }) =>
      salesLeadService.register(id, input),
    onSuccess: (result) => onLead(result.lead),
  })
}

export function useUpdateRegisteredSalesLead() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadUpdateRegisteredInput }) =>
      salesLeadService.updateRegistered(id, input),
    onSuccess: (result) => onLead(result.lead),
  })
}

/** Deal Done — acts on the registered record; requires a RegisteredLead to exist. */
export function useCloseSalesLead() {
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadCloseInput }) =>
      salesLeadService.close(id, input),
    onSuccess: onLead,
  })
}

/** quotationId=null unselects. */
export function useSelectQuotation() {
  const qc = useQueryClient()
  const onLead = useUpsertOnSuccess()
  return useMutation({
    mutationFn: ({ id, quotationId }: { id: string; quotationId: string | null }) =>
      salesLeadService.selectQuotation(id, quotationId),
    onSuccess: (lead) => {
      onLead(lead)
      void qc.invalidateQueries({ queryKey: ['quotations'] })
      void qc.invalidateQueries({ queryKey: ['quotation'] })
    },
  })
}

export function useDeleteSalesLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => salesLeadService.delete(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: salesLeadKeys.detail(id) })
      invalidateSalesLeadQueries(qc)
    },
  })
}
